'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { useChat } from '@/contexts/ChatContext'
import { cn } from '@/lib/utils'

/**
 * Full-screen drag-and-drop overlay.
 * Listens for file drags anywhere on the page. When a file is dragged over,
 * shows a fullscreen overlay prompting the user to drop. On drop, opens the
 * chat panel (if closed) and passes the file via ChatContext.droppedFile.
 */
export function GlobalDropOverlay() {
  const { openChat, chatOpen, setDroppedFile } = useChat()
  const [isDragging, setIsDragging] = useState(false)
  const dragCounterRef = useRef(0)

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault()
    // Only show overlay for files, not text/links
    if (!e.dataTransfer?.types.includes('Files')) return
    dragCounterRef.current++
    if (dragCounterRef.current === 1) {
      setIsDragging(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault()
    dragCounterRef.current--
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0
      setIsDragging(false)
    }
  }, [])

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy'
    }
  }, [])

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    dragCounterRef.current = 0
    setIsDragging(false)

    const file = e.dataTransfer?.files?.[0]
    if (!file) return

    // Pass the file to ChatContext
    setDroppedFile(file)

    // Open chat if it's closed
    if (!chatOpen) {
      openChat(null)
    }
  }, [chatOpen, openChat, setDroppedFile])

  useEffect(() => {
    window.addEventListener('dragenter', handleDragEnter)
    window.addEventListener('dragleave', handleDragLeave)
    window.addEventListener('dragover', handleDragOver)
    window.addEventListener('drop', handleDrop)

    return () => {
      window.removeEventListener('dragenter', handleDragEnter)
      window.removeEventListener('dragleave', handleDragLeave)
      window.removeEventListener('dragover', handleDragOver)
      window.removeEventListener('drop', handleDrop)
    }
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop])

  if (!isDragging) return null

  return (
    <div
      className={cn(
        'fixed inset-0 z-[100] flex flex-col items-center justify-center',
        'bg-atrium-background/80 backdrop-blur-sm',
        'border-2 border-dashed border-accent-600/50',
        'transition-opacity duration-150',
      )}
    >
      {/* Icon */}
      <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-accent-600/10">
        <svg className="h-8 w-8 text-accent-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 16.5V9.75m0 0l3 3m-3-3l-3 3M6.75 19.5a4.5 4.5 0 01-1.41-8.775 5.25 5.25 0 0110.233-2.33 3 3 0 013.758 3.848A3.752 3.752 0 0118 19.5H6.75z" />
        </svg>
      </div>

      {/* Text */}
      <p className="text-xl font-semibold text-atrium-black">Drop file to add to conversation</p>
      <p className="mt-1 text-sm text-atrium-black/50">CSV, TXT, images, and more</p>
    </div>
  )
}
